// Shared vocabulary for the Surveys module: question shapes and status
// derivation used by the survey builder and survey list.

export const QUESTION_TYPES = {
  SECTION: "section",
  INSTRUCTION: "instruction",
  SHORT_TEXT: "short_text",
  LONG_TEXT: "long_text",
  YES_NO: "yes_no",
  DROPDOWN: "dropdown",
  SINGLE_CHOICE: "single_choice",
  MULTI_CHOICE: "multi_choice",
  RATING: "rating",
  NUMERIC: "numeric",
};

// Structural items organize/annotate the survey but aren't answerable
// questions — excluded from "required question" validation, Question ID
// numbering, and response/answer rendering.
export const STRUCTURAL_QUESTION_TYPES = [QUESTION_TYPES.SECTION, QUESTION_TYPES.INSTRUCTION];

export const CHOICE_QUESTION_TYPES = [
  QUESTION_TYPES.DROPDOWN,
  QUESTION_TYPES.SINGLE_CHOICE,
  QUESTION_TYPES.MULTI_CHOICE,
];

// Left-column "Clinical Survey Components" palette, in display order. Two
// entries share the "rating" type with different scaleMin/scaleMax presets
// rather than duplicating the rating renderer for two near-identical types.
export const QUESTION_PALETTE = [
  { key: "section", type: QUESTION_TYPES.SECTION, label: "Section Heading", description: "Organize survey questions" },
  { key: "instruction", type: QUESTION_TYPES.INSTRUCTION, label: "Instruction Text", description: "Participant guidance" },
  { key: "short_text", type: QUESTION_TYPES.SHORT_TEXT, label: "Short Answer", description: "Single-line response" },
  { key: "long_text", type: QUESTION_TYPES.LONG_TEXT, label: "Long Answer", description: "Clinical free-text response" },
  { key: "yes_no", type: QUESTION_TYPES.YES_NO, label: "Yes / No", description: "Binary response" },
  { key: "dropdown", type: QUESTION_TYPES.DROPDOWN, label: "Dropdown", description: "Single selection" },
  { key: "single_choice", type: QUESTION_TYPES.SINGLE_CHOICE, label: "Multiple Choice", description: "One option from a list" },
  { key: "multi_choice", type: QUESTION_TYPES.MULTI_CHOICE, label: "Select All That Apply", description: "Multiple responses" },
  { key: "rating_5", type: QUESTION_TYPES.RATING, label: "5-Point Rating Scale", description: "Rating from 1 to 5", defaults: { scaleMin: 1, scaleMax: 5 } },
  { key: "rating_10", type: QUESTION_TYPES.RATING, label: "0–10 Severity Scale", description: "Symptom severity rating", defaults: { scaleMin: 0, scaleMax: 10 } },
  { key: "numeric", type: QUESTION_TYPES.NUMERIC, label: "Numeric Response", description: "Numeric participant entry" },
];

// Short uppercase header shown on each question card, matching the
// clinical-survey-builder reference design.
export const CARD_HEADER_LABELS = {
  [QUESTION_TYPES.SECTION]: "SECTION",
  [QUESTION_TYPES.INSTRUCTION]: "INSTRUCTION",
  [QUESTION_TYPES.SHORT_TEXT]: "SHORT ANSWER",
  [QUESTION_TYPES.LONG_TEXT]: "LONG ANSWER",
  [QUESTION_TYPES.YES_NO]: "YES/NO",
  [QUESTION_TYPES.DROPDOWN]: "DROPDOWN",
  [QUESTION_TYPES.SINGLE_CHOICE]: "MULTIPLE CHOICE",
  [QUESTION_TYPES.MULTI_CHOICE]: "SELECT ALL",
  [QUESTION_TYPES.RATING]: "SCALE",
  [QUESTION_TYPES.NUMERIC]: "NUMERIC",
};

export const CLINICAL_PURPOSE_OPTIONS = [
  { value: "Daily check-in", label: "Daily check-in" },
  { value: "Weekly check-in", label: "Weekly check-in" },
  { value: "Post-procedure follow-up", label: "Post-procedure follow-up" },
  { value: "Medication adherence", label: "Medication adherence" },
  { value: "Custom", label: "Custom" },
];

// Matches the SaveSurvey action lifecycle: 1 = draft, 2 = published, 3 = archived.
export const SURVEY_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
};

export const STATUS_CHIP_CONFIG = {
  [SURVEY_STATUS.DRAFT]: { label: "Draft", color: "default" },
  [SURVEY_STATUS.PUBLISHED]: { label: "Published", color: "success" },
  [SURVEY_STATUS.ARCHIVED]: { label: "Archived", color: "warning" },
};

// No "All Statuses" entry here — SelectInput already renders its own
// disabled placeholder MenuItem for an empty value (see FieldLabel/
// SelectInput convention used everywhere else); adding a second value=""
// option here would collide with it and render the box blank instead.
export const SURVEY_STATUS_FILTER_OPTIONS = Object.values(SURVEY_STATUS).map((value) => ({
  value,
  label: STATUS_CHIP_CONFIG[value].label,
}));

// The API's GetAllSurveys/GetSurveyById responses are normalized to a lowercase
// `status` string (see apiCalls/surveyBuilder.js) before reaching the UI.
export const getSurveyStatus = (survey) => survey?.status || SURVEY_STATUS.DRAFT;

export const isAssignable = (survey) => getSurveyStatus(survey) === SURVEY_STATUS.PUBLISHED;

// GetSurveyAssignments' status filter: 0 = all, 1 = Assigned, 2 = Completed, 3 = Revoked.
// No "All Statuses" entry, same reasoning as SURVEY_STATUS_FILTER_OPTIONS above.
export const SURVEY_ASSIGNMENT_STATUS_FILTER_OPTIONS = [
  { value: 1, label: "Assigned" },
  { value: 2, label: "Completed" },
  { value: 3, label: "Revoked" },
];

// Keyed by statusName (lowercased), not the numeric status code — the live
// backend's codes don't reliably match the documented 1/2/3 (see
// SurveyAssignModal's ACTIVE_ASSIGNMENT_STATUS_NAMES), so statusName is the
// only signal worth keying off of.
export const ASSIGNMENT_STATUS_CHIP_CONFIG = {
  assigned: { label: "Assigned", color: "info" },
  completed: { label: "Completed", color: "success" },
  revoked: { label: "Revoked", color: "error" },
};
